"use client";
import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { toast } from "react-hot-toast";
import { Logo } from "./Logo";
import Image from "next/image";
import {
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon,
  HomeIcon,
  AcademicCapIcon,
  BookOpenIcon,
  UserGroupIcon,
  ArrowRightOnRectangleIcon,
  ChatBubbleLeftRightIcon,
  HeartIcon,
  SparklesIcon,
  QuestionMarkCircleIcon,
  CreditCardIcon,
  EnvelopeIcon,
  ArrowDownTrayIcon,
  EllipsisHorizontalIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";
import { AuthStorage } from "@/lib/storage";
import { User } from "@/types";
import { useAuthSession } from "@/hooks/useAuthSession";



const Header: React.FC = () => {
  const pathname = usePathname();
  const { user, setUser, isLoading } = useAuthSession();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const userMenuRef = useRef<HTMLDivElement>(null);

  // Re-check auth when pathname changes (useful after login/navigation)
  // V2 Migration: Reliance on useAuthSession hook instead of manual storage sync
  useEffect(() => {
    // Only verify we have user state, the hook handles the source of truth
    if (!isLoading && !user) {
      // Optional: Trigger a check if needed, but useAuthSession checkAuth is usually sufficient
    }
  }, [pathname, isLoading, user]);

  useEffect(() => {
    // Close user menu when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);





  const handleLogout = async () => {
    try {
      // Set loading state immediately
      setIsLoggingOut(true);

      // Dispatch global logout loading event
      window.dispatchEvent(new CustomEvent("logout-loading", { detail: true }));

      // Get user data BEFORE clearing (to determine logout endpoint)
      const userData = AuthStorage.getUser();
      let logoutUrl = "/api/auth/logout";

      // Determine the logout endpoint based on user role
      if (userData?.role === "INSTRUCTOR") {
        logoutUrl = "/api/instructors/logout";
      }

      console.log(
        "Logging out user:",
        userData?.email,
        "Role:",
        userData?.role,
        "URL:",
        logoutUrl
      );

      // FIRST: Call logout API to clear server-side cookies
      try {
        const response = await fetch(logoutUrl, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          console.error("Logout API failed with status:", response.status);
        } else {
          console.log("✅ Server-side logout successful");
        }
      } catch (apiError) {
        console.error("Logout API error:", apiError);
        // Continue with client-side logout even if API fails
      }

      // THEN: Clear local state
      AuthStorage.clearAuth();

      // ALSO clear localStorage (for instructor registrations)
      if (typeof window !== "undefined") {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("user");
        localStorage.removeItem("token");
      }

      setUser(null);
      setIsUserMenuOpen(false);
      setIsMenuOpen(false);

      // Dispatch custom event to notify other components
      window.dispatchEvent(new Event("auth-change"));

      // Show success message
      toast.success("Signed out successfully!", { duration: 2000 });

      // Small delay to show toast
      await new Promise((resolve) => setTimeout(resolve, 500));

      // FINALLY: Force redirect to home page using window.location for hard navigation
      window.location.href = "/";
    } catch (error) {
      console.error("Logout error:", error);
      // Force clear and redirect even on error
      AuthStorage.clearAuth();
      setUser(null);
      window.location.href = "/";
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const navLinks = [
    { href: "/", label: "Home", icon: HomeIcon },
    { href: "/classes", label: "Classes", icon: AcademicCapIcon },
    { href: "/instructors", label: "Instructors", icon: UserGroupIcon },
    { href: "/community", label: "Community", icon: UserGroupIcon },
    { href: "/blogs", label: "Blogs", icon: BookOpenIcon },
  ];

  const userMenuItems = user
    ? [
      {
        label: "Profile",
        href: "/profile",
        icon: UserCircleIcon,
      },
      {
        label: "Prana AI",
        href: "/ai-assistant",
        icon: SparklesIcon,
      },
      // {
      //   label: "My Plan",
      //   href: "/my-plan",
      //   icon: CalendarDaysIcon,
      // },
      {
        label: "Wishlist",
        href: "/wishlist",
        icon: HeartIcon,
      },
      {
        label: "Payments",
        href: "/payments",
        icon: CreditCardIcon,
      },
      {
        label: "Chat",
        href: "/chat",
        icon: ChatBubbleLeftRightIcon,
      },
      {
        label: isLoggingOut ? "Signing out..." : "Sign Out",
        onClick: handleLogout,
        icon: ArrowRightOnRectangleIcon,
        isLoading: isLoggingOut,
      },
    ]
    : [];

  return (
    <>
      <header className="fixed top-0 left-0 w-full bg-white border-b border-[#463a33]/10 z-50 font-['Catamaran',sans-serif]">
        <div className="container mx-auto px-4 md:px-12 zoom-container">
          <div className="flex items-center justify-between h-[60px] sm:h-[90px]">
            {/* Logo */}
            <Link href="/" className="flex items-center z-50">
              <Image
                src="/images/logo.png"
                alt="Relaxing Routine"
                width={700}
                height={200}
                className="w-[150px] sm:w-[200px] h-auto"
                priority
                unoptimized
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-[#463a33] capitalize text-[14px] px-4 py-2 hover:bg-[#f7f7f7] rounded transition"
                >
                  {link.label}
                </Link>
              ))}

              {isLoading ? (
                <div className="ml-4 w-10 h-10 rounded-full bg-gray-100 animate-pulse" />
              ) : user ? (
                <div className="relative ml-4" ref={userMenuRef}>
                  <button
                    onClick={toggleUserMenu}
                    className="flex items-center justify-center w-10 h-10 rounded-full bg-[#f7f7f7] hover:bg-[#FF7043] hover:text-white transition-colors duration-200"
                    aria-label="User menu"
                  >
                    <UserCircleIcon className="w-6 h-6" />
                  </button>

                  {/* User Dropdown Menu */}
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 border border-[#463a33]/10">
                      <div className="px-4 py-2 border-b border-[#463a33]/10">
                        <p className="text-sm font-medium text-[#463a33]">
                          {user.name}
                        </p>
                        <p className="text-xs text-[#463a33]/60">{user.email}</p>
                      </div>
                      {userMenuItems.map((item) =>
                        item.href ? (
                          <Link
                            key={item.label}
                            href={item.href}
                            onClick={() => setIsUserMenuOpen(false)}
                            className="flex items-center px-4 py-2 text-sm text-[#463a33] hover:bg-[#f7f7f7] transition-colors duration-200"
                          >
                            <item.icon className="w-5 h-5 mr-2" />
                            {item.label}
                          </Link>
                        ) : (
                          <button
                            key={item.label}
                            onClick={() => {
                              item.onClick?.();
                              setIsUserMenuOpen(false);
                            }}
                            disabled={item.isLoading}
                            className={`flex items-center w-full px-4 py-2 text-sm text-[#463a33] hover:bg-[#f7f7f7] transition-colors duration-200 ${item.isLoading ? "opacity-70 cursor-wait" : ""
                              }`}
                          >
                            <item.icon
                              className={`w-5 h-5 mr-2 ${item.isLoading ? "animate-spin" : ""
                                }`}
                            />
                            {item.label}
                          </button>
                        )
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Link
                    href="/login"
                    className="bg-[#FF7043] text-white capitalize px-6 py-2.5 text-[14px] rounded-full transition-all hover:bg-[#463a33] hover:shadow-lg hover:-translate-y-0.5 font-medium shadow-md shadow-orange-500/20"
                  >
                    Login
                  </Link>
                </div>
              )}
            </nav>

            {/* Mobile Menu Button - HIDDEN, replaced by Bottom Nav */}
            {/* <button
            onClick={toggleMenu}
            className="md:hidden p-1.5 text-[#463a33] hover:bg-[#f7f7f7] rounded transition z-50"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <XMarkIcon className="w-6 h-6" />
            ) : (
              <Bars3Icon className="w-6 h-6" />
            )}
          </button> */}
          </div>

          {/* Mobile Navigation - Backdrop Overlay */}
          {isMenuOpen && (
            <div
              className="md:hidden fixed inset-0 bg-black/50 z-[60] top-0"
              onClick={() => setIsMenuOpen(false)}
            />
          )}

          {/* Mobile Navigation - "More" Bottom Sheet */}
          <div
            className={`md:hidden fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-[0_-8px_30px_rgba(0,0,0,0.12)] z-[80] transform transition-transform duration-300 ease-in-out max-h-[85vh] overflow-y-auto pb-4 ${isMenuOpen ? "translate-y-0" : "translate-y-full"
              }`}
            style={{ paddingBottom: 'calc(4rem + env(safe-area-inset-bottom))' }} // Add padding for bottom nav
          >
            {/* Sheet Handle */}
            <div className="flex justify-center pt-3 pb-1" onClick={() => setIsMenuOpen(false)}>
              <div className="w-12 h-1.5 bg-gray-300 rounded-full"></div>
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-2">
              <span className="text-lg font-bold text-[#463a33]">More Options</span>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close menu"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-6">
              {/* User Section - Prominent Card */}
              {isLoading ? (
                <div className="h-16 w-full bg-gray-100 rounded-2xl animate-pulse" />
              ) : user ? (
                <div className="bg-orange-50/50 rounded-2xl p-4 border border-orange-100 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm text-orange-500">
                    <UserCircleIcon className="w-8 h-8" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 truncate">{user.name}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  <Link
                    href="/login"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex flex-col items-center justify-center gap-2 p-4 bg-[#FF7043] text-white rounded-2xl shadow-sm active:scale-95 transition-transform"
                  >
                    <UserCircleIcon className="w-6 h-6" />
                    <span className="font-semibold text-sm">Login</span>
                  </Link>
                </div>
              )}

              {/* Grid Navigation Items */}
              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-1">Menu</h3>
                <div className="grid grid-cols-4 gap-4">
                  {/* Filter Main Nav Items & Map Rest */}
                  {navLinks.filter(link => !['/', '/classes', '/instructors', '/ai-assistant'].includes(link.href)).map((link) => {
                    const isActive = pathname === link.href;
                    return (
                      <Link
                        key={link.href}
                        href={link.href}
                        onClick={() => setIsMenuOpen(false)}
                        className="flex flex-col items-center gap-2 group"
                      >
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors duration-200 shadow-sm border border-gray-100 ${isActive
                          ? "bg-[#FF7043] text-white shadow-orange-100"
                          : "bg-gray-50 text-gray-600 group-hover:bg-[#FF7043] group-hover:text-white"
                          }`}>
                          <link.icon className="w-6 h-6" />
                        </div>
                        <span className={`text-[11px] font-medium text-center leading-tight ${isActive ? "text-[#FF7043]" : "text-gray-600"
                          }`}>{link.label}</span>
                      </Link>
                    );
                  })}

                  {/* User Specific Grid Items (if logged in) */}
                  {user && userMenuItems.filter(item => item.href && item.href !== '/ai-assistant').map((item) => {
                    const isActive = pathname === item.href;
                    return (
                      <Link
                        key={item.label}
                        href={item.href!}
                        onClick={() => setIsMenuOpen(false)}
                        className="flex flex-col items-center gap-2 group"
                      >
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors duration-200 shadow-sm border border-gray-100 ${isActive
                          ? "bg-[#FF7043] text-white shadow-orange-100"
                          : "bg-gray-50 text-gray-600 group-hover:bg-[#FF7043] group-hover:text-white"
                          }`}>
                          <item.icon className="w-6 h-6" />
                        </div>
                        <span className={`text-[11px] font-medium text-center leading-tight ${isActive ? "text-[#FF7043]" : "text-gray-600"
                          }`}>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Installation & Logout Area */}
              <div className="space-y-3">


                {/* Logout Button */}
                {userMenuItems.find(i => !i.href) && (
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      setTimeout(() => { userMenuItems.find(i => !i.href)?.onClick?.(); }, 100);
                    }}
                    disabled={isLoggingOut}
                    className="w-full flex items-center justify-center gap-2 p-3 text-red-500 font-medium hover:bg-red-50 rounded-xl transition-colors"
                  >
                    <ArrowRightOnRectangleIcon className="w-5 h-5" />
                    <span>Sign Out</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Bottom Navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-[9999] pb-safe shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
        <div className="flex items-center justify-around h-16">
          <Link
            href="/"
            className={`flex flex-col items-center justify-center w-full h-full space-y-1 ${pathname === "/" ? "text-[#FF7043]" : "text-gray-500 hover:text-[#FF7043]"
              }`}
          >
            <HomeIcon className="w-6 h-6" />
            <span className="text-[10px] font-medium">Home</span>
          </Link>
          <Link
            href="/classes"
            className={`flex flex-col items-center justify-center w-full h-full space-y-1 ${pathname === "/classes" ? "text-[#FF7043]" : "text-gray-500 hover:text-[#FF7043]"
              }`}
          >
            <AcademicCapIcon className="w-6 h-6" />
            <span className="text-[10px] font-medium">Classes</span>
          </Link>
          <Link
            href="/instructors"
            className={`flex flex-col items-center justify-center w-full h-full space-y-1 ${pathname === "/instructors" ? "text-[#FF7043]" : "text-gray-500 hover:text-[#FF7043]"
              }`}
          >
            <UserGroupIcon className="w-6 h-6" />
            <span className="text-[10px] font-medium">Instructors</span>
          </Link>
          <Link
            href="/ai-assistant"
            className={`relative flex flex-col items-center justify-center w-full h-full space-y-1 group ${pathname === "/ai-assistant" ? "text-[#FF7043]" : "text-gray-500"
              }`}
          >
            {/* Animated gradient background */}
            <div className="absolute inset-0 bg-gradient-to-t from-orange-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

            {/* Pulsing glow effect */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-orange-400/20 rounded-full blur-xl animate-pulse"></div>

            {/* Icon with special styling */}
            <div className="relative">
              <SparklesIcon className={`w-6 h-6 transition-all duration-300 ${pathname === "/ai-assistant"
                ? "text-[#FF7043] drop-shadow-[0_0_8px_rgba(255,112,67,0.6)]"
                : "text-orange-500 group-hover:text-[#FF7043] group-hover:scale-110 group-hover:rotate-12"
                }`} />
              {/* Notification dot */}
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full border border-white animate-pulse shadow-lg"></div>
            </div>

            {/* Text with gradient */}
            <span className={`text-[10px] font-bold relative z-10 transition-all duration-300 ${pathname === "/ai-assistant"
              ? "bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent"
              : "text-orange-500 group-hover:text-[#FF7043]"
              }`}>
              Prana AI
            </span>
          </Link>
          <button
            onClick={() => setIsMenuOpen(true)}
            className={`flex flex-col items-center justify-center w-full h-full space-y-1 ${isMenuOpen || !['/', '/classes', '/instructors', '/ai-assistant'].includes(pathname)
              ? "text-[#FF7043]"
              : "text-gray-500 hover:text-[#FF7043]"
              }`}
          >
            <EllipsisHorizontalIcon className="w-6 h-6" />
            <span className="text-[10px] font-medium">More</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Header;
